import { motion } from "framer-motion";
import { Youtube, Eye, Clock3, ArrowUpRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const VIDEOS = [
  {
    id: "HxWPuFQmWpM",
    title: "SAP Funds Management — Budget & Availability Control",
    meta: "36K views",
    duration: "34:36",
    tag: "Funds Management course",
  },
  {
    id: "PzadgcHnwGw",
    title: "SAP BP Configuration — Customers & Vendors in S/4HANA",
    meta: "22K views",
    duration: "56:50",
    tag: "FICO live training",
  },
  {
    id: "hrAUuUB5Rv0",
    title: "SAP S/4HANA Beginners Course — Session 1 Recording",
    meta: "5.6K views",
    duration: "1:44:03",
    tag: "FI Beginners course",
  },
];

const YoutubeShowcase = () => {
  return (
    <section className="relative py-24 lg:py-32" data-testid="youtube-showcase">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-amber-600 mb-4 flex items-center gap-2"
        >
          <Youtube size={13} /> Free previews
        </motion.p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight max-w-2xl"
          >
            Watch a real lesson before you enroll
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
            className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-md"
          >
            These are actual sessions from the classroom — the same trainer, the same depth you get inside every paid course.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VIDEOS.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.12 }}
              className="group rounded-3xl border border-slate-200 bg-white overflow-hidden hover:-translate-y-1 hover:border-red-400/40 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300"
              data-testid={`youtube-video-${i}`}
            >
              <div className="aspect-video bg-slate-100">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${v.id}`}
                  title={v.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <div className="p-6">
                <p className="font-mono2 text-[9px] uppercase tracking-[0.18em] text-red-600 mb-3">{v.tag}</p>
                <h3 className="font-display text-base font-bold leading-snug mb-4">{v.title}</h3>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Eye size={12} /> {v.meta}</span>
                  <span className="flex items-center gap-1.5"><Clock3 size={12} /> {v.duration}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
          className="mt-12 flex justify-center"
        >
          <a
            href="https://www.youtube.com/@VikramFotani"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 border border-slate-200 rounded-full px-8 py-4 text-sm font-semibold text-slate-700 hover:border-red-400/60 hover:text-red-600 transition-colors duration-300"
            data-testid="youtube-subscribe-button"
          >
            <Youtube size={16} /> 100+ more free lessons on YouTube <ArrowUpRight size={15} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default YoutubeShowcase;
